import { TTreeState } from '@infotech-soe/merps/types/Queries';
import { TDatabaseId, tg } from '@infotech-soe/metaschema';
import { iter } from '@metarhia/common';
import isNumber from 'lodash/isNumber';

import { AppDispatch } from '../..';
import { DEFAULT_PRESET } from '../../../constants/dataset';
import { DatasetConfig } from '../../../models/user-config/DisplayModeConfig';
import CategoryService from '../../../services/category/CategoryService';
import ICategoryService from '../../../services/category/ICategoryService';
import { CategoryTreeType } from '../../../services/category/view-model/CategoryViewModel';
import { RefreshStrategy } from '../../../services/commands/wrap/CategoryCommand';
import DatasetService from '../../../services/dataset/DatasetService';
import DisplayModeDatasetService from '../../../services/dataset/display-mode/DisplayModeDatasetService';
import IDisplayModeDatasetService from '../../../services/dataset/display-mode/IDisplayModeDatasetService';
import IDatasetService from '../../../services/dataset/IDatasetService';
import { DatasetGridRow } from '../../../services/dataset/view-model/DatasetGridRow';
import { UpdateDatasetResult } from '../../../services/dataset/view-model/UpdateDatasetResult';
import UniqueId from '../../../services/uid/UniqueId';
import orderBy from '../../../ui/components/dataset/utils/orderBy';
import { AppState } from '../../reducers/root';
import { getCatalogsStateEntity } from '../../selectors/catalogs';
import {
  getAllChildrenDatasetsFromCategory,
  getCategoryConditions,
  getCategoryState,
  getCategoryTreeType,
  isDatasetVisible,
} from '../../selectors/category';
import {
  filterDatasetsForCategory,
  getDatasetActiveRowId,
  getDatasetCategoryId,
  getDatasetConfig,
  getDatasetConfigCurrentPreset,
  getDatasetIdFields,
  getDatasetRows,
  getDatasetState,
  isDatasetOpen,
  isDatasetRoot,
} from '../../selectors/dataset';
import { isDictionaryOpenForCategory } from '../../selectors/dictionary';
import { getHierarchyStateEntity } from '../../selectors/hierarchy';
import { getWorkTableState } from '../../selectors/workTable';
import catalogs from '../../slices/catalogs';
import { CategoryTargetingInfo } from '../../slices/category';
import commands from '../../slices/commands';
import dataset, { DatasetSwitchEffect } from '../../slices/dataset';
import dictionary from '../../slices/dictionary';
import form from '../../slices/form';
import hierarchy from '../../slices/hierarchy';
import workTable, { OpenWorkTablePostEffectType } from '../../slices/workTable';
import { CatalogsTreeStateMarshaller } from '../../utils/CatalogsTreeStateMarshaller';
import {
  apiCall,
  createAppListener,
  postDispatch,
  postDispatchAny,
} from '../listenerHelpers';

const datasetListener = createAppListener();

type OptionalTreeState = TTreeState | undefined;

const categoryService: ICategoryService = new CategoryService();
const displayModeDatasetService: IDisplayModeDatasetService =
  new DisplayModeDatasetService();
const datasetService: IDatasetService = new DatasetService();
const batchCreateResolvers: Map<UniqueId, () => void> = new Map();

const getTreeState = (
  datasetId: UniqueId,
  state: AppState
): OptionalTreeState => {
  const datasetState = getDatasetState(state, datasetId);

  if (datasetState.catalogContext !== null) {
    return datasetState.catalogContext.treeState;
  }
};

const updateDataset = (
  datasetId: UniqueId,
  state: AppState
): Promise<UpdateDatasetResult> => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const categoryState = getCategoryState(state, categoryId);
  const datasets = filterDatasetsForCategory(state, categoryId);

  return datasetService.updateDataset(
    categoryState,
    datasets,
    datasetId,
    getCategoryConditions(state, categoryId),
    getTreeState(datasetId, state),
    datasets[datasetId].config.sort
  );
};

const updateDependentTree = (
  state: AppState,
  dispatch: AppDispatch,
  datasetId: UniqueId,
  activeRowId: TDatabaseId
): void => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const { treeType } = getCategoryState(state, categoryId);
  const treeState = getTreeState(datasetId, state);
  const { locateNode } = getDatasetConfigCurrentPreset(state, datasetId);

  if (
    treeType !== CategoryTreeType.NONE &&
    locateNode &&
    treeState?.hierarchy &&
    isDatasetRoot(state, datasetId)
  ) {
    const { rowIdField, treeField } = getDatasetIdFields(state, datasetId);
    const treeNodeId = getDatasetRows(state, datasetId).find(
      r => r[rowIdField] === activeRowId
    )?.[treeField ?? ''];
    const { activeId, nodes } =
      treeType === CategoryTreeType.CATALOGS
        ? getCatalogsStateEntity(state, categoryId)
        : getHierarchyStateEntity(state, categoryId);
    const isNodeExist = nodes.some(n => n.id === treeNodeId);
    if (
      tg.isSomething(treeNodeId) &&
      tg.isSomething(activeId) &&
      activeId !== treeNodeId
    ) {
      if (treeType === CategoryTreeType.CATALOGS) {
        dispatch(
          isNodeExist
            ? catalogs.selectNode({
                catalogId: categoryId,
                nodeId: treeNodeId,
              })
            : catalogs.locateNode({
                catalogId: categoryId,
                nodeId: activeId,
                treeId: treeNodeId,
              })
        );
      } else if (treeType === CategoryTreeType.HIERARCHY) {
        dispatch(
          isNodeExist
            ? hierarchy.selectNode({ categoryId, nodeId: treeNodeId })
            : hierarchy.locateNode({
                categoryId,
                nodeId: treeNodeId,
                forceDOMFocus: true,
              })
        );
      }
    }
  }
};

const updateDependentDatasets = (
  state: AppState,
  dispatch: AppDispatch,
  datasetId: UniqueId,
  effect: DatasetSwitchEffect,
  targetingInfo?: CategoryTargetingInfo | undefined
): void => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const { datasets } = getCategoryState(state, categoryId);

  dispatch(commands.get({ datasetId }));

  const ds = getAllChildrenDatasetsFromCategory(state, datasetId);

  for (const id of ds) {
    if (!isDatasetOpen(state, id) || !isDatasetVisible(state, id)) {
      dispatch(dataset.invalidate({ datasetId: id }));
      continue;
    }

    if (datasets[id].parent === datasetId) {
      dispatch(dataset.update({ datasetId: id, targetingInfo }));
    }
  }

  if (isDictionaryOpenForCategory(state, categoryId)) {
    dispatch(dictionary.updateOutputs({ id: categoryId }));
  }

  switch (effect) {
    case DatasetSwitchEffect.REVIEW_FORM:
      dispatch(form.updateReviewForm({ categoryId, datasetId }));
      break;
    case DatasetSwitchEffect.WORK_TIMES: {
      const worker = getWorkTableState(state);

      if (worker.changed) {
        dispatch(workTable.update());
      }

      dispatch(
        workTable.open({
          datasetId,
          tableType: worker.tableType,
          effect: { type: OpenWorkTablePostEffectType.REPLACE },
        })
      );

      break;
    }
  }
};

const prepareInitialCatalogContext = (
  datasetId: UniqueId,
  state: AppState,
  dispatch: AppDispatch
): void => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const categoryState = getCategoryState(state, categoryId);
  const datasetDefinition = categoryState.datasets[datasetId];

  if (datasetDefinition.parent !== null) {
    return;
  }

  let tree;
  if (categoryState.treeType === CategoryTreeType.CATALOGS) {
    tree = getCatalogsStateEntity(state, categoryId);
  } else if (categoryState.treeType === CategoryTreeType.HIERARCHY) {
    tree = getHierarchyStateEntity(state, categoryId);
  }

  if (tree) {
    dispatch(dataset.updateCatalogContext({ datasetId }));
  }
};

const getRecords = async (
  datasetId: UniqueId,
  ids: TDatabaseId[],
  state: AppState
): Promise<Map<TDatabaseId, DatasetGridRow | undefined>> => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const { source, datasets } = getCategoryState(state, categoryId);
  return iter(
    await Promise.all(
      ids.map(async id => [
        id,
        await datasetService.getRecordById(
          source.category,
          source.displayMode,
          datasets[datasetId].name,
          id
        ),
      ])
    )
  ).collectTo<Map<TDatabaseId, DatasetGridRow | undefined>>(Map);
};

const getHeader = async (
  datasetId: string,
  state: AppState
): Promise<string | undefined> => {
  const categoryId = getDatasetCategoryId(state, datasetId);
  const categoryState = getCategoryState(state, categoryId);
  const datasets = filterDatasetsForCategory(state, categoryId);

  return datasetService.getHeader(categoryState, datasets, datasetId);
};

datasetListener.startListening(
  apiCall(
    {
      request: dataset.createBatch,
      success: dataset.createBatchSuccess,
      failure: dataset.createBatchFailure,
    },
    async (payload, _state, dispatch) => {
      const { categoryId, datasets, displayModeConfig } = payload;

      const promises = Object.entries(datasets).map(
        ([datasetId, { name }]) =>
          new Promise<void>(resolve => {
            batchCreateResolvers.set(datasetId, resolve);
            dispatch(
              dataset.create({
                categoryId,
                datasetId,
                datasetConfig: displayModeConfig?.datasetsConfig?.[name],
              })
            );
          })
      );

      await Promise.all(promises);

      return {
        ...payload,
        treeActiveId: displayModeConfig?.treeActiveId,
        treeCheckedIds: displayModeConfig?.treeCheckedIds,
      };
    }
  )
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.create,
      success: dataset.createSuccess,
      failure: dataset.createFailure,
    },
    async ({ categoryId, datasetId, datasetConfig }, state) => {
      const categoryState = getCategoryState(state, categoryId);

      const {
        columns,
        idField,
        source,
        treeField,
        editableFields,
        defaultSort,
      } = await datasetService.createDataset(
        categoryState.source,
        categoryState.datasets[datasetId]
      );

      const config: DatasetConfig = datasetConfig
        ? await displayModeDatasetService.sanitizeDatasetConfig(
            categoryState,
            datasetId,
            datasetConfig,
            defaultSort
          )
        : {
            columnsWidth: {},
            columnsOrder: [],
            pinned: 0,
            showForm: false,
            currentPreset: DEFAULT_PRESET,
            preset: {},
            sort: defaultSort,
          };

      return {
        categoryId,
        datasetId,
        columns,
        treeField,
        rowIdField: idField,
        config,
        source,
        editableFields,
        hideCheckbox: categoryState.source.hideCheckbox,
      };
    }
  )
);

datasetListener.startListening(
  postDispatch(dataset.createSuccess, ({ datasetId }, state, dispatch) => {
    prepareInitialCatalogContext(datasetId, state, dispatch);
    dispatch(dataset.updateRecordViewMap({ datasetId }));
    dispatch(commands.get({ datasetId }));

    const resolver = batchCreateResolvers.get(datasetId);
    if (resolver) {
      resolver();
      batchCreateResolvers.delete(datasetId);
    }
  })
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.update,
      success: dataset.updateSuccess,
      failure: dataset.updateFailure,
    },
    async (payload, state) => {
      const { targetingInfo, datasetId } = payload;
      const {
        rows,
        rowsFullCount,
        activeRowId: currentActiveRowId,
        totals,
        header,
      } = await updateDataset(datasetId, state);

      const activeRowId =
        targetingInfo?.datasetIds?.[datasetId] || currentActiveRowId;

      return {
        ...payload,
        rows,
        rowsFullCount,
        activeRowId,
        totals,
        header,
      };
    }
  )
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.addRows,
      success: dataset.updateSuccess,
      failure: dataset.updateFailure,
    },
    async ({ datasetId, ids, formId }, state) => {
      const {
        rows: oldRows,
        rowIdField,
        activeRowId,
        rowsFullCount,
        totals,
      } = getDatasetState(state, datasetId);
      const newRows = [
        ...(await getRecords(datasetId, ids, state)).values(),
      ].filter(x => x !== undefined) as DatasetGridRow[];
      const currentPos =
        oldRows.findIndex(r => r[rowIdField] === activeRowId) || 0;

      const rows = [
        ...oldRows.slice(0, currentPos + 1),
        ...newRows,
        ...oldRows.slice(currentPos + 1),
      ];

      return {
        datasetId,
        rows,
        rowsFullCount: rowsFullCount + newRows.length,
        activeRowId:
          newRows.length > 0
            ? newRows[newRows.length - 1][rowIdField]
            : activeRowId,
        formId,
        header: await getHeader(datasetId, state),
        totals,
        totalsInvalid: true,
      };
    }
  )
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.updateRows,
      success: dataset.updateSuccess,
      failure: dataset.updateFailure,
    },
    async ({ datasetId, ids, formId }, state) => {
      const {
        rows: oldRows,
        rowIdField,
        activeRowId,
        totals,
      } = getDatasetState(state, datasetId);
      const newRows = await getRecords(datasetId, ids, state);

      const rows = oldRows
        .map(row =>
          newRows.has(row[rowIdField])
            ? (newRows.get(row[rowIdField]) as DatasetGridRow)
            : row
        )
        .filter(x => x !== undefined);

      return {
        datasetId,
        rows,
        rowsFullCount: rows.length,
        activeRowId: activeRowId ?? undefined,
        formId,
        header: await getHeader(datasetId, state),
        totals,
        totalsInvalid: true,
      };
    }
  )
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.sort,
      success: dataset.sortSuccess,
      failure: dataset.sortFailure,
    },
    async ({ datasetId }, state) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const datasets = filterDatasetsForCategory(state, categoryId);
      const { rowsFullCount: rowsServerCount, rows: rowsClient } =
        datasets[datasetId];

      let result;

      const { sort } = getDatasetConfig(state, datasetId);

      if (rowsServerCount !== rowsClient.length || !sort.length) {
        const conditions = categoryState.source.displayModeConditions;

        const { rows, rowsFullCount } = await datasetService.updateDataset(
          categoryState,
          datasets,
          datasetId,
          conditions,
          getTreeState(datasetId, state),
          sort
        );

        result = {
          datasetId,
          rows,
          rowsFullCount,
        };
      } else {
        const sortColumns: string[] = [];
        const sortDirection: string[] = [];
        sort.forEach(columnSortModel => {
          sortColumns.push(columnSortModel.field);
          sortDirection.push(columnSortModel.dir.toLocaleLowerCase());
        });
        result = {
          datasetId,
          rows: rowsClient
            .slice()
            .sort(orderBy(sortColumns, sortDirection as Array<'asc' | 'desc'>)),
          rowsFullCount: rowsServerCount,
        };
      }

      return result;
    }
  )
);

datasetListener.startListening(
  postDispatch(dataset.dispose, ({ datasetId }, _state, dispatch) => {
    dispatch(commands.dispose({ datasetId }));
  })
);

datasetListener.startListening(
  postDispatch(
    dataset.selectPreviousRecord,
    ({ datasetId, effect }, state, dispatch) => {
      const { activeRowId, rows, rowIdField } = getDatasetState(
        state,
        datasetId
      );

      const currentIndex = rows.findIndex(
        row => row[rowIdField] === activeRowId
      );
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : rows.length - 1;
      const rowId = rows[prevIndex][rowIdField];

      dispatch(dataset.changeActiveRow({ datasetId, rowId, effect }));
    }
  )
);

datasetListener.startListening(
  postDispatch(
    dataset.selectNextRecord,
    ({ datasetId, effect }, state, dispatch) => {
      const { activeRowId, rows, rowIdField } = getDatasetState(
        state,
        datasetId
      );

      const currentIndex = rows.findIndex(
        row => row[rowIdField] === activeRowId
      );
      const prevIndex = currentIndex < rows.length - 1 ? currentIndex + 1 : 0;
      const rowId = rows[prevIndex][rowIdField];

      dispatch(dataset.changeActiveRow({ datasetId, rowId, effect }));
    }
  )
);

datasetListener.startListening(
  postDispatch(
    dataset.changeSelectedIds,
    ({ datasetId }, state, dispatch, prevState) => {
      const categoryId = getDatasetCategoryId(prevState, datasetId);
      if (isDictionaryOpenForCategory(state, categoryId)) {
        dispatch(dictionary.updateOutputs({ id: categoryId }));
      }
    }
  )
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.removeRows,
      success: dataset.updateSuccess,
      failure: dataset.updateFailure,
    },
    async ({ datasetId, ids }, state) => {
      const {
        rowIdField,
        activeRowId,
        rowsFullCount,
        rows: oldRows,
        totals,
      } = getDatasetState(state, datasetId);

      let prevIndex = 0;
      const rows = oldRows.filter((row, index) => {
        prevIndex = row[rowIdField] === activeRowId ? index : prevIndex;
        return !ids.includes(row[rowIdField]);
      });

      const isPrevActiveRemoved =
        ids.length === 0 ||
        (isNumber(activeRowId) && ids.includes(activeRowId));
      const newActiveRowId = rows.length
        ? isPrevActiveRemoved
          ? rows[Math.min(Math.max(prevIndex, 0), rows.length - 1)][rowIdField]
          : activeRowId
        : undefined;

      return {
        datasetId,
        rows,
        rowsFullCount: rowsFullCount - ids.length,
        activeRowId: newActiveRowId,
        header: await getHeader(datasetId, state),
        totals,
        totalsInvalid: true,
      };
    }
  )
);

datasetListener.startListening(
  postDispatchAny(
    [dataset.findRecord, dataset.changeActiveRow],
    ({ datasetId, rowId, effect }, state, dispatch, prevState) => {
      if (
        prevState.dataset[datasetId] &&
        prevState.dataset[datasetId].activeRowId !== rowId
      ) {
        updateDependentDatasets(state, dispatch, datasetId, effect);
        updateDependentTree(state, dispatch, datasetId, rowId);
      }
    }
  )
);

datasetListener.startListening(
  postDispatch(
    dataset.updateSuccess,
    ({ datasetId, formId, forceDOMFocus, targetingInfo }, state, dispatch) => {
      updateDependentDatasets(
        state,
        dispatch,
        datasetId,
        DatasetSwitchEffect.NOTHING,
        targetingInfo
      );

      const currentActiveId = getDatasetActiveRowId(state, datasetId);
      if (currentActiveId) {
        updateDependentTree(state, dispatch, datasetId, currentActiveId);
      }

      if (formId) {
        dispatch(form.close({ formId }));
      }

      if (forceDOMFocus) {
        datasetService.focus(datasetId);
      }
    }
  )
);

datasetListener.startListening(
  postDispatch(
    dataset.updateCatalogContext,
    ({ datasetId }, state, dispatch) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const tree =
        getCategoryTreeType(state, categoryId) === CategoryTreeType.CATALOGS
          ? getCatalogsStateEntity(state, categoryId)
          : getHierarchyStateEntity(state, categoryId);

      dispatch(
        dataset.setCatalogContext({
          datasetId,
          catalogContext: {
            activeId: tree.activeId,
            treeState: new CatalogsTreeStateMarshaller(tree).getTreeState(),
          },
        })
      );
    }
  )
);

datasetListener.startListening(
  postDispatch(
    dataset.refreshWithStrategy,
    ({ datasetId, strategy, ids }, _state, dispatch) => {
      if (!strategy || strategy === RefreshStrategy.ALL) {
        dispatch(dataset.update({ datasetId, forceDOMFocus: true }));
      } else if (strategy === RefreshStrategy.REMOVE) {
        dispatch(dataset.removeRows({ datasetId, ids }));
      } else if (strategy === RefreshStrategy.NEW) {
        dispatch(dataset.addRows({ datasetId, ids }));
      } else if (strategy === RefreshStrategy.CURRENT) {
        dispatch(dataset.updateRows({ datasetId, ids }));
      }
    }
  )
);

datasetListener.startListening(
  postDispatch(
    dataset.updateConfig,
    ({ datasetId, needToUpdate }, state, dispatch) => {
      if (needToUpdate) {
        dispatch(dataset.update({ datasetId }));
      }
      const { category: categoryState, dataset: datasetState } = state;
      const categoryStateEntry =
        categoryState[datasetState[datasetId].categoryId];
      void categoryService.saveCategory(categoryStateEntry, datasetState);
    }
  )
);

datasetListener.startListening(
  apiCall(
    {
      request: dataset.updateRecordViewMap,
      success: dataset.updateRecordViewMapSuccess,
      failure: dataset.updateRecordViewMapFailure,
    },
    async ({ datasetId }, state) => {
      const categoryId = getDatasetCategoryId(state, datasetId);
      const categoryState = getCategoryState(state, categoryId);
      const recordViewMap = await datasetService.getRecordViewMap(
        categoryState,
        datasetId
      );
      return { datasetId, recordViewMap };
    }
  )
);

export default datasetListener.middleware;
